import { Checkbox, Line } from '@mm/components';
import { getRecurly, useRecurlyForm } from '@mm/hooks';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { useFeatureFlagsCRUDActions } from '@mm/state/actions/FeatureFlagsCRUDActions';
import mainMenuCRUDActions from '@mm/state/actions/MainMenuCRUDActions';
import subscriptionCRUDActions, {
  CreateProSubscriptionPayload,
  CreateTeamSubscriptionPayload,
  DowngradeOrCancelPayload,
  UpgradePayload,
  useSubscriptionCRUDActions,
} from '@mm/state/actions/SubscriptionCRUDActions';
import { CouponObject } from '@mm/state/controllers/SubscriptionsController';
import { PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { getPlanPrice } from '@mm/state/helpers/get-plan-price';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import {
  EnhancedPlan,
  PlanSubtypes,
  PlanTypeBE,
} from '@mm/state/interfaces/subscription';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import CompanyModel from '@mm/state/model/CompanyModel';
import {
  isBilling2023Plan,
  isBilling2023Subscription,
  isBilling2023V2Plan,
  SubscriptionModel,
} from '@mm/state/model/SubscriptionModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectPlansAllotments } from '@mm/state/selectors/facebookPageSelector';
import {
  selectAccountDetails,
  selectCreditsForFuturePayments,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  activeChatbotSelector,
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { centsToDollars } from '@mm/utils';
import { gtag } from '@mm/plugins/GoogleAnalytics';
import {
  SafetyCertificateTwoTone,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { Formik, FormikProps } from 'formik';
import { isEmpty, isNil, isNumber } from 'lodash';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import countries from 'react-phone-number-input/modules/countries.json';
import states from '@mm/constants/states.json';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { useLocation } from 'react-router-dom';
import {
  ActionButton,
  ButtonAudacityLevel,
} from '../../../../../../common/buttons/ActionButton/ActionButton';
import ErrorMessage from '../../../../../../common/components/ErrorMessage/ErrorMessage';
import FormFieldWrapper from '../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { InputWithPlusMinusButtons } from '../../../../../../common/components/InputWithPlusMinusButtons/InputWithPlusMinusButtons';
import MainSelect from '../../../../../../common/components/MainSelect/MainSelect';
import { WrappedTextInput } from '../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { REFRESH } from '../../../../../../common/constants/routes';
import { PAGE_UPGRADE } from '../../../../../../common/helpers/plugins/constants';
import { trackFacebookEvent } from '../../../../../../common/helpers/plugins/facebook-pixel-init';
import { useCreateSMSKeyword } from '../../../../../../editor/features/smsKeywords/SMSKeywordsPanel/use-create-sms-keyword';
import { trackUpgradeEventGA } from '../../../../../../modules/MarketingAnalytics';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { Routes } from '../../../../../../routes/top-level-routes';
import PaymentMethodDetails from '../../../common/PaymentMethodDetails/PaymentMethodDetails';
import { AcceptTerms } from './AcceptTerms/AcceptTerms';
import CheckoutFormHelpers, {
  CheckoutFormValues,
} from './checkout-form-helpers';
import './CheckoutForm.pcss';
import { PaymentDisplayArea } from './PaymentDisplayArea/PaymentDisplayArea';
import { getPriceByLevel } from './PriceGetterFunctions/GetPriceByLevel';
import { getPriceByPhoneNumber } from './PriceGetterFunctions/GetPriceByPhoneNumber';
import { getPriceByPlan } from './PriceGetterFunctions/GetPriceByPlan';
import { getPriceBySeats } from './PriceGetterFunctions/GetPriceBySeats';
import { getPriceByTeamPlan } from './PriceGetterFunctions/GetPriceByTeamPlan';
import RedeemCoupon from './RedeemCoupon/RedeemCoupon';
import { useCreditCardFormState } from './use-credit-card-form-state';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { Spin } from 'antd';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';

type OuterProps = {
  facebookPage?: EnhancedBillingPage;
  plan: EnhancedPlan;
  onPlanCreatedCallback: (
    subscription?: SubscriptionModel,
    closeModalCalled?: boolean,
  ) => void;
  company: CompanyModel;
  upgradeFromSubscriptionId?: number;
  downgradeFromSubscriptionId?: number;
  setIsFetching: (isFetching: boolean) => void;
  landmineOpenId?: number;
  level?: {
    id: number;
    name: string;
    level: number;
  };
  updateAllotment?: (
    selectedLevel: number,
    subscriptionId: number,
    companyId: number,
  ) => void;
  manualRedirect?: boolean;
  paywall?: boolean;
  seatsObject?: {
    userSeats: number;
    extraUserSeats: number;
  };
  phoneNumber?: string;
  subscription?: SubscriptionModel;
  activeFacebookPagePlanInfo: Partial<PlanInfo>;
  fetchBasicPageList?: (id?: number) => void;
  fetchPage?: (id?: number) => void;
  fetchMenu?: (chatbotId: number) => void;
  activeChatbot?: ChatbotModel | null | undefined;
  companyId?: number;
  stayOnPage?: boolean;
  navigatedFrom?: {
    location: string;
    search: string;
  };
  smsActivation?: boolean;
  isAnnual: boolean;
  onPlanCreatedFailed?: () => void;
  skipFetchActivePage?: boolean;
  extraSendsOnly?: boolean;
  alert?: ReactElement;
  showCompanySelect?: boolean;
  onCompanySelectChange?: (value: number) => void;
};

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const useAfterSubscriptionCreation = (props: Partial<Props>) => {
  const router = useRouter();
  const {
    onPlanCreatedCallback,
    fetchBasicPageList,
    fetchPage,
    fetchMenu,
    company,
    manualRedirect,
    facebookPage,
    activeChatbot,
    companyId,
    stayOnPage = true,
    skipFetchActivePage,
  } = props;

  const featureFlagsCRUDActions = useFeatureFlagsCRUDActions();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();

  const pollAvailableFeatures = () => {
    let pollingAttempts = 0;
    const pollInterval = 30000; // 30 seconds
    const maxAttempts = 3;

    const pollRequest = async () => {
      try {
        if (facebookPage) {
          const result =
            await featureFlagsCRUDActions.retrieveSubscriptionFeatures(
              facebookPage?.id,
            );

          if (result?.length) {
            return;
          }

          pollingAttempts += 1;

          if (pollingAttempts < maxAttempts) {
            setTimeout(pollRequest, pollInterval);
          } else {
            return;
          }
        }
      } catch (error) {
        throw new Error(`Request error: ${error}`);
      }
    };

    setTimeout(pollRequest, 5000);
  };

  return useCallback(
    (subscription?: SubscriptionModel) => {
      gtag('event', 'mobilemonkey-upgrade');

      trackFacebookEvent(PAGE_UPGRADE);

      trackUpgradeEventGA();

      // Reset cached data
      if (company && fetchBasicPageList) {
        fetchBasicPageList(company.id);
      }

      if (facebookPage && fetchPage && !skipFetchActivePage) {
        fetchPage(facebookPage.id);
      }

      if (facebookPage) {
        setTimeout(
          () => {
            subscriptionCRUDActions.getCurrentSubscription(
              facebookPage.id,
              false,
              subscription?.id,
              subscription && isBilling2023Subscription(subscription),
            );

            pollAvailableFeatures();
          },

          1500,
        );
      }

      if (activeChatbot?.id && fetchMenu) {
        fetchMenu(activeChatbot.id);
      }

      // close modal
      if (onPlanCreatedCallback) {
        onPlanCreatedCallback(subscription, false);
      }
      if (manualRedirect) {
        return;
      }
      if (stayOnPage) {
        router.history.push(`${REFRESH}${router.location.pathname}`);
        return;
      }

      let backLink = Routes.chatbotEditor.getLink();
      if (activeChatbot && activeChatbot.companyId === companyId) {
        backLink = BotBuilderRoutes.dashboard.getLink({
          chatbotId: activeChatbot.id,
        });
      }
      router.history.push(backLink);
    },
    [
      fetchBasicPageList,
      company?.id,
      facebookPage,
      onPlanCreatedCallback,
      router.history,
      fetchPage,
      activeChatbot,
      companyId,
    ],
  );
};

const CheckoutForm = (props: Props) => {
  const {
    facebookPage,
    createProSubscription,
    company,
    plan,
    paywall,
    createTeamSubscription,
    downgrade,
    upgradeFromSubscriptionId,
    upgrade,
    setIsFetching,
    downgradeFromSubscriptionId,
    landmineOpenId,
    level,
    seatsObject,
    onPlanCreatedCallback,
    phoneNumber,
    subscription,
    activeFacebookPagePlanInfo,
    fetchBasicPageList,
    fetchPage,
    fetchMenu,
    stayOnPage,
    navigatedFrom,
    smsActivation,
    manualRedirect,
    isAnnual,
    onPlanCreatedFailed,
    skipFetchActivePage,
    extraSendsOnly,
    alert,
    showCompanySelect,
    onCompanySelectChange,
  } = props;
  const dispatch = useDispatch();
  const { history } = useRouter();
  const { id: chatbotId } = useSelector(activeChatbotSelector);
  const [tokenizeError, setTokenizeError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [coupon, setCoupon] = useState<CouponObject | undefined>(undefined);
  const [seatsNumber, setSeatsNumber] = useState(
    seatsObject?.extraUserSeats || 0,
  );
  const [isDowngrade, setIsDowngrade] = useState(false);
  const [proRatedDueToday, setProRatedDueToday] = useState(0);
  const isUpgradeOrDowngrade =
    isNumber(upgradeFromSubscriptionId) || isNumber(upgradeFromSubscriptionId);
  const [allowsOverages, setAllowsOverages] = useState(true);
  const busyRef = useRef<boolean>(false);

  const companies = useSelector(selectCompanies);
  const accountDetails = useSelector(selectAccountDetails);
  const companyId = useSelector(selectActiveCompanyId);
  const futurePaymentCredits = useSelector(selectCreditsForFuturePayments);
  const activeChatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const session = useSelector(selectSessionSelector);

  const { createKeyword } = useCreateSMSKeyword();

  const isBilling2023PlanSubtype = isBilling2023Plan(plan);
  const isNewBillingv2Plan = isBilling2023V2Plan(plan);
  const isNewBillingSubscription =
    subscription && isBilling2023Subscription(subscription);

  const allPlansAllotments = useSelector(selectPlansAllotments);
  const formRef = useRef<HTMLFormElement>(null);
  const isTeam = plan.planType === PlanTypeBE.team;
  const companySelectVisible =
    (showCompanySelect || isTeam) && (companies?.length || 0) > 1;
  const creditCardFormState = useCreditCardFormState();
  const {
    sendOveragesEnabled,
    getCurrentSubscription,
    updateAllotment,
    submitSeatsNumber,
    getSubscription,
  } = useSubscriptionCRUDActions();

  const afterSubscriptionCreation = useAfterSubscriptionCreation({
    onPlanCreatedCallback,
    fetchBasicPageList,
    fetchPage,
    fetchMenu,
    manualRedirect,
    company,
    facebookPage,
    activeChatbot,
    companyId,
    stayOnPage,
    skipFetchActivePage,
  });

  useRecurlyForm();

  const retrieveSubscription = () => {
    if (!facebookPage) {
      return;
    }
    if (isNewBillingSubscription) {
      getSubscription(subscription.id, facebookPage.id);
    } else {
      getCurrentSubscription(facebookPage.id, false);
    }
  };

  const inputNumberChange = (value: number) => {
    if (!seatsObject || !subscription) {
      return;
    }
    setSeatsNumber(value);
    if (value === seatsObject.extraUserSeats) {
      setIsDowngrade(false);
      setProRatedDueToday(0);
      return;
    }
    if (value >= subscription.extraUserSeats) {
      setIsDowngrade(false);
      if (seatsObject.extraUserSeats > 0) {
        setProRatedDueToday(
          (value - seatsObject.extraUserSeats) *
            subscription.userSeatAddOnPrice *
            subscription.priceFactor,
        );
        return;
      }
      setProRatedDueToday(
        value * subscription.userSeatAddOnPrice * subscription.priceFactor,
      );
    } else {
      setIsDowngrade(true);
      setProRatedDueToday(0);
    }
  };

  const confirmPayment = useCallback(
    // tslint:disable-next-line: cyclomatic-complexity
    async (recurlyToken: string) => {
      const couponCode = coupon && coupon.code;
      if (busyRef.current) return;
      busyRef.current = true;
      const manageOverages = async subscription => {
        const isNewBillingSubscription =
          isBilling2023Subscription(subscription);
        const isBilling2023V2Subscription =
          isBilling2023Subscription(subscription);

        if (isBilling2023V2Subscription) return;

        if (
          subscription &&
          !subscription.overagesEnabled &&
          allowsOverages &&
          facebookPage?.id
        ) {
          await sendOveragesEnabled(
            subscription.id,
            allowsOverages,
            facebookPage.id,
            skipFetchActivePage,
            undefined,
            false,
            !!isNewBillingSubscription,
          );
        }
      };

      if (isTeam && !upgradeFromSubscriptionId) {
        const response = await createTeamSubscription(
          {
            recurlyToken,
            coupon: couponCode,
            facebookPageId: facebookPage?.id,
            companyId: company.id,
            planCode: plan.planCode,
            landmineOpenId,
          },
          plan,
        );

        if (!response) {
          onPlanCreatedFailed?.();
          return;
        }

        const subscription =
          response && 'newSubscription' in response
            ? response.newSubscription
            : response;

        manageOverages(subscription);
        afterSubscriptionCreation(subscription);
      } else if (upgradeFromSubscriptionId && (facebookPage || isTeam)) {
        const response = await upgrade(
          upgradeFromSubscriptionId,
          {
            planCode: plan.planCode,
            coupon: couponCode,
            facebookPageId: facebookPage ? facebookPage.id : undefined,
            companyId: company.id,
            landmineOpenId,
          },
          plan,
        );
        const subscription =
          response && 'newSubscription' in response
            ? response.newSubscription
            : response;

        if (!response) {
          onPlanCreatedFailed?.();
          return;
        }

        manageOverages(subscription);
        afterSubscriptionCreation(subscription);
      } else if (downgradeFromSubscriptionId && facebookPage) {
        await downgrade(
          company.id,
          downgradeFromSubscriptionId,
          {
            planCode: plan.planCode,
            coupon: couponCode,
            facebookPageId: facebookPage.id,
            companyId: company.id,
            pollResults: {},
          },
          false,
          subscription && isBilling2023Subscription(subscription),
          false,
          plan,
        );

        manageOverages(subscription);

        afterSubscriptionCreation(subscription);
      } else {
        const response = await createProSubscription(
          {
            recurlyToken,
            coupon: couponCode,
            companyId: company.id,
            planCode: plan.planCode,
            facebookPageId: facebookPage?.id,
            landmineOpenId,
          },
          plan,
        );

        if (!response) {
          onPlanCreatedFailed?.();
          return;
        }
        const subscription =
          response && 'newSubscription' in response
            ? response.newSubscription
            : response;

        manageOverages(subscription);
        afterSubscriptionCreation(subscription);
      }
    },
    [
      coupon,
      isTeam,
      upgradeFromSubscriptionId,
      facebookPage,
      downgradeFromSubscriptionId,
      createTeamSubscription,
      company.id,
      plan.planCode,
      afterSubscriptionCreation,
      upgrade,
      downgrade,
      createProSubscription,
      landmineOpenId,
      allowsOverages,
      sendOveragesEnabled,
      skipFetchActivePage,
      subscription,
      onPlanCreatedFailed,
    ],
  );
  const [phoneState, setPhoneState] = useState({
    phone: phoneNumber,
    isValid: !!phoneNumber,
  });
  const [selectedCompany, setSelectedCompany] = useState<number>(company.id);
  const companyCRUDActions = useCompanyCRUDActions();

  const submitForm = useCallback(async () => {
    if (phoneState.phone && phoneState.isValid) {
      await companyCRUDActions.update({
        id: companyId,
        phoneNumber: phoneState.phone,
      });
    }

    if (formRef.current) {
      const Recurly = await getRecurly();

      // Get token to represent credit card information and other form values
      Recurly.token(formRef.current, (error, token) => {
        if (error) {
          // These are validation errors from Recurly
          setTokenizeError(error.message);
        } else {
          // Set token and call confirmPayment() to redirect to the confirm payment page
          confirmPayment(token.id);
        }
      });
    }
  }, [
    formRef,
    setTokenizeError,
    confirmPayment,
    phoneState.phone,
    phoneState.isValid,
  ]);

  const confirmSeatsSelection = async (
    companyId: number,
    updateOverages: () => void,
  ) => {
    if (!subscription || !facebookPage) {
      return;
    }

    const res = await submitSeatsNumber(
      subscription.id,
      seatsNumber,
      companyId,
      facebookPage.id,
      isBilling2023Subscription(subscription),
    );
    if (res) {
      updateOverages();
      setTimeout(() => {
        retrieveSubscription();
      }, 1000);
      onPlanCreatedCallback(undefined, true);
    }
  };

  const confirmSendCreditsSelection = async (
    level: number,
    subscriptionId: number,
    companyId: number,
    updateOverages: () => void,
  ) => {
    let response;
    if (facebookPage) {
      response = await updateAllotment({
        level,
        subscriptionId,
        companyId,
        facebookPageId: facebookPage.id,
      });
      if (response?.success) {
        if (!extraSendsOnly) {
          updateOverages();
        }

        setTimeout(() => {
          retrieveSubscription();
        }, 1000);
      }
    }
    if (response?.success) {
      onPlanCreatedCallback(undefined, true);
    }
  };

  const confirmAddPhoneNumberSelection = async (
    subscriptionId: number,
    smsNumber: string,
    facebookPageId: number,
    companyId: number,
    updateOverages: () => void,
  ) => {
    const response = await dispatch(
      subscriptionCRUDActions.attachSMSNumber({
        subscriptionId,
        smsNumber,
        facebookPageId,
        companyId,
        skipFetchActivePage,
      }),
    );

    if (!response) {
      onPlanCreatedFailed?.();
      return;
    }
    updateOverages();

    setTimeout(() => {
      retrieveSubscription();
    }, 1000);
    onPlanCreatedCallback(undefined, true);
  };

  const pricesObject = useMemo(() => {
    if (subscription) {
      if (level && !isEmpty(level)) {
        return getPriceByLevel({
          plansAllotments: level,
          subscription,
          plan,
          futurePaymentCredits,
        });
      }
      if (!isEmpty(phoneNumber)) {
        return getPriceByPhoneNumber({
          plan,
          currentSubscription: subscription,
          futurePaymentCredits,
        });
      }
      if (!isEmpty(seatsObject)) {
        return getPriceBySeats({
          seatsNumber,
          isDowngrade,
          proRatedDueToday,
          currentSubscription: subscription,
          plan,
          futurePaymentCredits,
        });
      }
    }
    if (isTeam) {
      return getPriceByTeamPlan({
        plan,
        allPlansAllotments,
        coupon,
        futurePaymentCredits,
        subscriptions: accountDetails?.subscriptions,
      });
    }
    return getPriceByPlan({
      plan,
      allPlansAllotments,
      coupon,
      futurePaymentCredits,
      currentSubscription: subscription,
      downgradeFromSubscriptionId,
    });
  }, [
    accountDetails?.subscriptions,
    allPlansAllotments,
    coupon,
    futurePaymentCredits,
    downgradeFromSubscriptionId,
    isDowngrade,
    isTeam,
    level,
    phoneNumber,
    plan,
    proRatedDueToday,
    seatsNumber,
    seatsObject,
    subscription,
  ]);

  const planPrice = getPlanPrice(plan.unitAmountInCents, coupon);
  const { pathname } = useLocation();

  const onModalClose = () => {
    if (
      (activeFacebookPagePlanInfo.isCustomTeam ||
        activeFacebookPagePlanInfo.isLegacyTeamPlan) &&
      subscription &&
      subscription?.maxSendAllotment <= 0
    ) {
      dispatch(
        modalActions.openGeneralConfirmModal({
          headerText: 'Congratulations!',
          messageText: `Congratulations on getting started with Text Message Marketing! 
        It looks like your plan doesn’t include any SMS Credits. To begin sending Text Messages, you’ll need to purchase some.`,
          cancelButtonFirst: false,
          okButtonText: 'Purchase Credits',
          okButtonStyling: { blue: true },
          onCloseHandler: ok => {
            if (ok) {
              history.push('/settings/billing');
            }
          },
        }),
      );
    }

    if (
      smsActivation &&
      !stayOnPage &&
      pathname !==
        BotBuilderRoutes.automationHome.getLink({
          chatbotId,
        })
    ) {
      // Note: this is hacky way to make Back button navigate to the original location AFTER navigating to the newly created Keyword/Trigger
      if (chatbotId) {
        const fakePreviousPath = BotBuilderRoutes.chatStarters.getLink({
          chatbotId,
          kind: 'sms_keywords',
        });

        history.replace(fakePreviousPath, { allowRedirect: true });
      }
      createKeyword({ initialPhrase: 'GO' });
    }

    if (!isNil(navigatedFrom) && !isEmpty(navigatedFrom)) {
      history.push({
        pathname: navigatedFrom.location,
        search: navigatedFrom.search,
        state: { allowRedirect: true },
      });
    }
  };

  const onConfirmPayment = () => {
    if (busyRef.current) return;
    busyRef.current = true;

    const updateOverages = () => {
      const isBilling2023V2Subscription =
        subscription && isBilling2023Subscription(subscription);

      if (isBilling2023V2Subscription) return;

      if (
        subscription &&
        !subscription.overagesEnabled &&
        allowsOverages &&
        facebookPage?.id
      ) {
        sendOveragesEnabled(
          subscription.id,
          allowsOverages,
          facebookPage.id,
          skipFetchActivePage,
          undefined,
          false,
          !!isNewBillingSubscription,
        );
      }
    };

    if (level && !isEmpty(level) && subscription) {
      confirmSendCreditsSelection(
        level.level,
        subscription.id,
        company.id,
        updateOverages,
      );
      return;
    }
    if (!isEmpty(seatsObject)) {
      confirmSeatsSelection(
        company.id,

        updateOverages,
      );
      return;
    }
    if (phoneNumber && facebookPage && subscription) {
      confirmAddPhoneNumberSelection(
        subscription.id,
        phoneNumber,
        facebookPage.id,
        company.id,
        updateOverages,
      );
      onModalClose();
      return;
    }
    confirmPayment('');
    busyRef.current = false;
  };

  const onContactSalesClick = () => {
    dispatch(modalActions.openContactSalesModal({}));
  };

  const isActivatingSmsNumber =
    phoneNumber &&
    facebookPage &&
    subscription &&
    pricesObject &&
    Number(pricesObject.payNow) === 0 &&
    smsActivation;

  // SMS number activation - automatically confirm if price is 0$
  useEffect(() => {
    if (isActivatingSmsNumber) {
      onConfirmPayment();
    }
  }, [smsActivation, phoneNumber]);

  if (isActivatingSmsNumber) {
    return null;
  }

  const finishedOnboarding =
    session.userAttributes.onboardingSignupStep === OnboardingSignupStep.NONE;

  const overagesDisabled = !(
    facebookPage?.overagesEnabled ||
    subscription?.overagesEnabled ||
    isNewBillingv2Plan
  );

  /* tslint:disable:max-line-length */
  const overagesText = isBilling2023PlanSubtype
    ? 'Automatically charge me for overages after current allotment is exceeded. You will receive an email and your credit card will be charged for each overage.'
    : 'Automatically increase send credit limit when current allotment is exceeded? You will receive an email and your credit card will be charged for each new credit increase.';

  if (accountDetails && accountDetails.account) {
    return (
      <Spin
        size="small"
        spinning={busyRef.current}
        wrapperClassName="checkout-form__spin"
      >
        <div className="checkout-form">
          <div className="checkout-form__form">
            {alert}
            {companySelectVisible && (
              <FormFieldWrapper
                className="checkout-form__company-wrapper"
                label="Company"
              >
                <MainSelect
                  value={selectedCompany}
                  onChange={e => {
                    setSelectedCompany(Number(e.target.value));
                    onCompanySelectChange?.(Number(e.target.value));
                  }}
                  name={CheckoutFormHelpers.fields.company}
                  className="checkout-form__company-select"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </MainSelect>
              </FormFieldWrapper>
            )}
            {seatsObject && (
              <InputWithPlusMinusButtons
                className="checkout-form__seats-selector "
                onChange={inputNumberChange}
                initialValue={seatsObject.extraUserSeats}
                lowerBound={0}
              />
            )}
            {phoneNumber && (
              <div>
                <div className="checkout-form__phone-number">
                  <strong> New Phone Number: {phoneNumber}</strong>
                </div>
              </div>
            )}
            <PaymentDisplayArea
              pricesObject={pricesObject}
              plan={plan}
              isUpgradeOrDowngrade={isUpgradeOrDowngrade}
              isAnnual={isAnnual}
              hideUnchangedFields={extraSendsOnly}
            />
            <PaymentMethodDetails className="checkout-form__payment-method-details" />
            {pricesObject.showAddCoupon && !isBilling2023PlanSubtype && (
              <>
                <RedeemCoupon
                  className="checkout-form__coupon"
                  company={company}
                  planCode={plan.planCode}
                  onCouponRedeem={setCoupon}
                />
                <Line className="checkout-form__line" />
              </>
            )}

            <div className="checkout-form__checkboxes-area">
              {overagesDisabled && !extraSendsOnly && (
                <div className="checkout-form__overages-checkbox">
                  <Checkbox
                    isChecked={allowsOverages}
                    onToggle={() => setAllowsOverages(!allowsOverages)}
                  />
                  {overagesText}
                </div>
              )}
              <AcceptTerms
                isChecked={agreedToTerms}
                onAagreedToTerms={setAgreedToTerms}
                showMSA={!!phoneNumber}
                className="checkout-form__terms-area"
                disabled={busyRef.current}
              />
            </div>
          </div>
          <div className="checkout-form__charge-price-wrapper">
            <div className="checkout-form__charge-price">
              ${pricesObject.payNow?.toFixed(2).replace(/[.,]00$/, '')}
            </div>
            <ActionButton
              onClick={onConfirmPayment}
              className={classNames(
                'checkout-form__pay-button',
                agreedToTerms
                  ? 'checkout-form__pay-button--active'
                  : 'checkout-form__pay-button--disabled',
              )}
              audacityLevel={ButtonAudacityLevel.none}
              disabled={!agreedToTerms || busyRef.current}
              css={{
                buttonText: 'checkout-form__pay-button-text',
                buttonBackground: !agreedToTerms
                  ? 'checkout-form__pay-button-background--disabled'
                  : 'checkout-form__pay-button-background--active',
              }}
            >
              <ShoppingCartOutlined
                color="white"
                className="checkout-form__pay-button-icon"
              />
              Pay Now
            </ActionButton>
            <div className="checkout-form__charge-message">
              <SafetyCertificateTwoTone
                className="checkout-form__safety-certificate"
                color="#2F54EB"
                twoToneColor={[
                  'var(--color-accent-blue)',
                  'rgba(47,84,235,0.1)',
                ]}
              />
              Your card will be charged
            </div>
          </div>
          {finishedOnboarding && (
            <div
              className="checkout-form__contact-sales-link"
              onClick={onContactSalesClick}
            >
              Contact sales for enterprise plans
            </div>
          )}
        </div>
      </Spin>
    );
  }
  return (
    <Formik
      initialValues={CheckoutFormHelpers.getInitialValues(
        isTeam,
        company,
        session,
      )}
      validationSchema={CheckoutFormHelpers.getValidationSchema(isTeam)}
      onSubmit={submitForm}
    >
      {(formProps: FormikProps<CheckoutFormValues>) => (
        <form
          className="checkout-form"
          ref={formRef}
          onSubmit={formProps.handleSubmit}
        >
          <div className="checkout-form__form">
            {companySelectVisible && (
              <FormFieldWrapper
                className="checkout-form__company-wrapper"
                label="Company"
              >
                <MainSelect
                  value={formProps.values.company}
                  onChange={value => {
                    formProps.handleChange(value);
                    onCompanySelectChange?.(Number(value));
                  }}
                  name={CheckoutFormHelpers.fields.company}
                  className="checkout-form__company-select"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </MainSelect>
              </FormFieldWrapper>
            )}

            <div className="checkout-form__fields">
              <WrappedTextInput
                label="First Name"
                formProps={formProps}
                data-recurly="first_name"
                inputClassName="checkout-form__input"
                fieldName={CheckoutFormHelpers.fields.firstName}
              />

              <WrappedTextInput
                label="Last Name"
                formProps={formProps}
                data-recurly="last_name"
                inputClassName="checkout-form__input"
                fieldName={CheckoutFormHelpers.fields.lastName}
              />
            </div>
            <WrappedTextInput
              className="checkout-form__input-wrapper"
              label="Email"
              formProps={formProps}
              data-recurly="email"
              inputClassName="checkout-form__input"
              fieldName={CheckoutFormHelpers.fields.email}
            />

            <Line className="checkout-form__line" />

            <FormFieldWrapper
              className="checkout-form__input-wrapper fs-hide"
              label="Card Info"
              touched={
                creditCardFormState.touched && !creditCardFormState.focused
              }
              error={creditCardFormState.error}
            >
              <div className="checkout-form__card-input" data-recurly="card" />
            </FormFieldWrapper>

            <FormFieldWrapper
              className="checkout-form__input-wrapper"
              label="Country"
            >
              <MainSelect
                value={formProps.values.country}
                onChange={e => {
                  formProps.handleChange(e);
                  formProps.setFieldValue('state', '');
                }}
                name={CheckoutFormHelpers.fields.country}
                data-recurly="country"
                className="checkout-form__country-select"
              >
                {countries.map(country => (
                  <option key={country[0]} value={country[0]}>
                    {country[1]}
                  </option>
                ))}
              </MainSelect>
            </FormFieldWrapper>

            <Line className="checkout-form__line" />

            <WrappedTextInput
              label="Address Line 1"
              formProps={formProps}
              data-recurly="address1"
              inputClassName="checkout-form__input"
              fieldName={CheckoutFormHelpers.fields.address1}
              placeholder="Street address, P.O box, company name"
            />

            <WrappedTextInput
              label="Address Line 2"
              formProps={formProps}
              data-recurly="address2"
              inputClassName="checkout-form__input"
              fieldName={CheckoutFormHelpers.fields.address2}
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
            <div className="checkout-form__fields">
              <WrappedTextInput
                label="City"
                formProps={formProps}
                data-recurly="city"
                inputClassName="checkout-form__input"
                fieldName={CheckoutFormHelpers.fields.city}
              />

              {formProps.values.country === 'US' ? (
                <FormFieldWrapper
                  className="checkout-form__input-wrapper"
                  label="State"
                >
                  <MainSelect
                    value={formProps.values.state}
                    onChange={formProps.handleChange}
                    name={CheckoutFormHelpers.fields.state}
                    data-recurly="state"
                    className="checkout-form__country-select"
                  >
                    {states.map(states => (
                      <option
                        key={states.abbreviation}
                        value={states.abbreviation}
                      >
                        {states.name}
                      </option>
                    ))}
                  </MainSelect>
                </FormFieldWrapper>
              ) : (
                <WrappedTextInput
                  label="State"
                  formProps={formProps}
                  data-recurly="state"
                  inputClassName="checkout-form__input"
                  fieldName={CheckoutFormHelpers.fields.state}
                />
              )}

              <WrappedTextInput
                label="ZIP Code"
                formProps={formProps}
                data-recurly="postal_code"
                inputClassName="checkout-form__input"
                fieldName={CheckoutFormHelpers.fields.postalCode}
              />
            </div>

            <Line className="checkout-form__line" />

            {!isBilling2023PlanSubtype && (
              <>
                <RedeemCoupon
                  company={company}
                  planCode={plan.planCode}
                  onCouponRedeem={setCoupon}
                />
                <Line className="checkout-form__line" />
              </>
            )}

            <input type="hidden" name="recurly-token" data-recurly="token" />

            {overagesDisabled && (
              <div className="checkout-form__overages-checkbox">
                <Checkbox
                  isChecked={allowsOverages}
                  onToggle={() => setAllowsOverages(!allowsOverages)}
                />
                {overagesText}
              </div>
            )}

            <div className="checkout-form__terms">
              <AcceptTerms
                isChecked={formProps.values.terms || agreedToTerms}
                onAagreedToTerms={() => {
                  formProps.setFieldValue('terms', !formProps.values.terms);
                  setAgreedToTerms(!agreedToTerms);
                }}
                showMSA={!!phoneNumber}
                className="checkout-form__terms-area"
              />
            </div>
          </div>
          {tokenizeError && (
            <ErrorMessage
              error={tokenizeError}
              className="checkout-form__input-error"
            />
          )}
          <div className="checkout-form__charge-price">
            ${centsToDollars(planPrice).replace(/[.,]00$/, '')}
            <span className="checkout-form__charge-price-billing-period">
              /{plan.billingPeriod}
            </span>
          </div>
          <ActionButton
            type="submit"
            className={classNames(
              'checkout-form__pay-button',
              formProps.isValid && !creditCardFormState.error && agreedToTerms
                ? 'checkout-form__pay-button--active'
                : 'checkout-form__pay-button--disabled',
            )}
            audacityLevel={ButtonAudacityLevel.none}
            disabled={
              !formProps.isValid ||
              !!creditCardFormState.error ||
              !agreedToTerms ||
              busyRef.current
            }
            css={{
              buttonText: 'checkout-form__pay-button-text',
              buttonBackground:
                formProps.isValid &&
                !creditCardFormState.error &&
                !agreedToTerms
                  ? 'checkout-form__pay-button-background--disabled'
                  : 'checkout-form__pay-button-background--active',
            }}
          >
            <ShoppingCartOutlined
              color="white"
              className="checkout-form__pay-button-icon"
            />
            {planPrice ? `Pay Now` : 'Confirm'}
          </ActionButton>

          {finishedOnboarding && (
            <div
              className="checkout-form__contact-sales-link-marginless"
              onClick={onContactSalesClick}
            >
              Contact sales for enterprise plans
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  upgrade: (companyId: number, payload: UpgradePayload, plan: EnhancedPlan) =>
    dispatch(subscriptionCRUDActions.upgrade(companyId, payload, plan)),
  createProSubscription: (
    payload: CreateProSubscriptionPayload,
    plan: EnhancedPlan,
  ) => dispatch(subscriptionCRUDActions.createProSubscription(payload, plan)),
  createTeamSubscription: (
    payload: CreateTeamSubscriptionPayload,
    plan: EnhancedPlan,
  ) => dispatch(subscriptionCRUDActions.createTeamSubscription(payload, plan)),
  fetchBasicPageList: (companyId: number) =>
    dispatch(
      connectedPageCRUDActions.retrieveBasicListByCompany(
        companyId,
        {
          withFacebookErrors: true,
          withSubscription: true,
        },
        true,
      ),
    ),
  fetchMenu: (chatbotId: number) =>
    dispatch(mainMenuCRUDActions.retrieveAll(chatbotId)),
  fetchPage: (facebookPageId: number) =>
    dispatch(connectedPageCRUDActions.retrievePage(facebookPageId)),
  downgrade: (
    companyId: number,
    subscriptionId: number,
    payload: DowngradeOrCancelPayload,
    loadContactsCount?: boolean,
    useV2Route?: boolean,
    isCancel?: boolean,
    plan?: EnhancedPlan,
  ) =>
    dispatch(
      subscriptionCRUDActions.downgrade(
        companyId,
        subscriptionId,
        payload,
        loadContactsCount,
        useV2Route,
        isCancel,
        plan,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

export default enhance(CheckoutForm);
